import { useEffect, useRef, useState } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { ModalStyled } from 'src/components';
import type { ReceiveModalFlowStep } from 'src/types';

import lottie from 'src/images/lotties/receive-modal.json';

type Props = {
  open: boolean;
  onClose: () => void;
  steps: ReceiveModalFlowStep[];
  customMaxWidth?: number;
  hideCloseButton?: boolean;
};

export const ReceiveModalFlow = ({
  open,
  onClose,
  steps,
  customMaxWidth = 620,
  hideCloseButton = false,
}: Props) => {
  const prevStepRef = useRef<number>(0);
  const [step, setStep] = useState(0);
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);

  useEffect(() => {
    if (open) {
      setStep(0);
    }
  }, [open]);

  useEffect(() => {
    if (lottieRef.current) {
      if (step === 1) {
        if (prevStepRef.current === 3) {
          lottieRef.current.playSegments([39, 0], true);
        } else {
          lottieRef.current.goToAndStop(0, true);
        }
      } else if (step === 2) {
        lottieRef.current.playSegments([0, 39], true);
      } else if (step === 4) {
        lottieRef.current.playSegments([39, 72], true);
      }
    }
  }, [lottieRef, step]);

  const flowStep = steps[step] ?? {};

  const onPrimary = async () => {
    await flowStep.primaryButtonFunction?.();

    if (typeof flowStep.goToStepAfterPrimary === 'number') {
      prevStepRef.current = step;
      setStep(
        Math.min(Math.max(flowStep.goToStepAfterPrimary, 0), steps.length - 1),
      );
    }
  };

  const onSecondary = async () => {
    await flowStep.secondaryButtonFunction?.();

    if (typeof flowStep.goToStepAfterSecondary === 'number') {
      prevStepRef.current = step;

      setStep(
        Math.min(
          Math.max(flowStep.goToStepAfterSecondary, 0),
          steps.length - 1,
        ),
      );
    }
  };

  return (
    <ModalStyled
      open={open}
      onClose={onClose}
      closeOnOutsideClick={false}
      showCloseButton={!hideCloseButton}
      aria-labelledby='modal-flow-title'
      aria-describedby='modal-flow-description'
      customMaxWidth={customMaxWidth}
    >
      <div className='flex w-full flex-col items-center justify-center gap-4 p-6 py-10 sm:gap-6 sm:p-8'>
        <div className='flex w-fit flex-col items-center justify-center gap-6 sm:gap-8'>
          {flowStep.mainImage ? (
            <img
              loading='lazy'
              src={flowStep.mainImage}
              alt='modal'
              className='mx-auto w-full rounded xs:max-w-[320px]'
            />
          ) : (
            <Lottie
              lottieRef={lottieRef}
              animationData={lottie}
              loop={false}
              autoplay={false}
              className='m-auto w-full xs:w-[350px]'
            />
          )}

          <div className='flex flex-col items-center justify-center gap-4 sm:gap-6'>
            {flowStep.title && (
              <h2 className='heading-300 text-pretty text-center'>
                {flowStep.title}
              </h2>
            )}

            {flowStep.description && (
              <p
                id='modal-flow-description'
                className='text-300 text-pretty text-center'
              >
                {flowStep.description}
              </p>
            )}

            {flowStep.secondaryImage && (
              <img
                loading='lazy'
                src={flowStep.secondaryImage}
                alt='instruction'
                className='mx-auto w-full xs:max-w-[380px] sm:max-w-[420px]'
              />
            )}
          </div>

          {(flowStep.primaryButtonText || flowStep.secondaryButtonText) && (
            <div className='flex flex-wrap items-center justify-center gap-3'>
              {flowStep.primaryButtonText && (
                <button
                  onClick={onPrimary}
                  className='button-primary px-6 py-3'
                >
                  {flowStep.primaryButtonText}
                </button>
              )}
              {flowStep.secondaryButtonText && (
                <button
                  onClick={onSecondary}
                  className='button-secondary px-6 py-3'
                >
                  {flowStep.secondaryButtonText}
                </button>
              )}
            </div>
          )}
        </div>

        {flowStep.afterButtonsBlock && <>{flowStep.afterButtonsBlock}</>}
      </div>
    </ModalStyled>
  );
};
